/**
 * Contact List View Queries
 *
 * React Query hooks for displaying contact data in list views.
 * Uses centralized mock data with localStorage persistence.
 */

import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { persistedContactListViews, persistedContactRecords } from '../mockData/contacts';
import { persistedClientRecords } from '../mockData/clients';
import { getRecordById, getRecordsForObjectPage } from '../recordStorageService';
import { shouldUseGobodo } from '../../utils/gobodoUtils';
import { USE_RDS_FOR_CORE_OBJECTS, isRdsCoreObject } from '../../config/rdsConfig';
import { normalizeRecordForListView } from '../../utils/listViewRecordNormalizer';
import type { ListView, ListViewRecord, ListViewData } from '../../types/listView';

// Mock list views for Contacts
const mockContactListViews: ListView[] = [
  {
    id: 'lv_contacts_all',
    name: 'All Contacts',
    apiName: 'all_contacts',
    objectApiName: 'Contact',
    description: 'All contact records',
    isDefault: true,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Contact Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'title', fieldLabel: 'Title', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'clientName', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 3 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 4 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 5 },
    ],
    createdBy: 'System',
    createdDate: '2024-01-01T00:00:00Z'
  },
  {
    id: 'lv_contacts_my',
    name: 'My Contacts',
    apiName: 'my_contacts',
    objectApiName: 'Contact',
    description: 'Contacts I own',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Contact Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'title', fieldLabel: 'Title', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'clientName', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 3 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 4 },
      { fieldApiName: 'lastContact', fieldLabel: 'Last Contact', fieldType: 'date', isVisible: true, order: 5 },
    ],
    filters: [
      { fieldApiName: 'owner', operator: 'equals', value: 'Current User' }
    ],
    createdBy: 'Admin',
    createdDate: '2024-02-10T09:00:00Z'
  },
  {
    id: 'lv_contacts_recent',
    name: 'Recently Contacted',
    apiName: 'recent_contacts',
    objectApiName: 'Contact',
    description: 'Contacts contacted in the last 7 days',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Contact Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'clientName', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'lastContact', fieldLabel: 'Last Contact', fieldType: 'date', isVisible: true, order: 2 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 3 },
      { fieldApiName: 'phone', fieldLabel: 'Phone', fieldType: 'phone', isVisible: true, order: 4 },
      { fieldApiName: 'owner', fieldLabel: 'Owner', fieldType: 'text', isVisible: true, order: 5 },
    ],
    sortBy: {
      fieldApiName: 'lastContact',
      direction: 'desc'
    },
    createdBy: 'Admin',
    createdDate: '2024-03-05T11:30:00Z'
  },
  {
    id: 'lv_contacts_active',
    name: 'Active Contacts',
    apiName: 'active_contacts',
    objectApiName: 'Contact',
    description: 'Contacts with Active status',
    isDefault: false,
    columns: [
      { fieldApiName: 'name', fieldLabel: 'Contact Name', fieldType: 'text', isVisible: true, order: 0 },
      { fieldApiName: 'title', fieldLabel: 'Title', fieldType: 'text', isVisible: true, order: 1 },
      { fieldApiName: 'clientName', fieldLabel: 'Client', fieldType: 'text', isVisible: true, order: 2 },
      { fieldApiName: 'status', fieldLabel: 'Status', fieldType: 'badge', isVisible: true, order: 3 },
      { fieldApiName: 'email', fieldLabel: 'Email', fieldType: 'email', isVisible: true, order: 4 },
      { fieldApiName: 'mobile', fieldLabel: 'Mobile', fieldType: 'phone', isVisible: true, order: 5 },
    ],
    filters: [
      { fieldApiName: 'status', operator: 'equals', value: 'Active' }
    ],
    createdBy: 'Admin',
    createdDate: '2024-04-12T14:15:00Z'
  }
];

// Mock contact records
const mockContactRecords: ListViewRecord[] = [
  {
    id: 'contact_001',
    name: 'Jennifer Taylor',
    title: 'Chief Technology Officer',
    account: 'Tech Innovations Inc',
    email: 'j.taylor@techinnovations.com',
    phone: '(555) 789-0123',
    mobile: '(555) 789-0124',
    status: 'Active',
    owner: 'John Smith',
    department: 'Executive',
    lastContact: '2025-01-15T14:30:00Z',
    createdDate: '2024-03-10T09:00:00Z'
  },
  {
    id: 'contact_002',
    name: 'Robert Chen',
    title: 'Operations Manager',
    account: 'Global Manufacturing Corp',
    email: 'r.chen@globalmanuf.com',
    phone: '(555) 890-1234',
    mobile: '(555) 890-1235',
    status: 'Active',
    owner: 'Sarah Johnson',
    department: 'Operations',
    lastContact: '2025-01-14T11:00:00Z',
    createdDate: '2024-04-15T10:30:00Z'
  },
  {
    id: 'contact_003',
    name: 'Amanda Foster',
    title: 'Marketing Director',
    account: 'Retail Solutions LLC',
    email: 'a.foster@retailsolutions.com',
    phone: '(555) 901-2345',
    mobile: '(555) 901-2346',
    status: 'Active',
    owner: 'Michael Chen',
    department: 'Marketing',
    lastContact: '2025-01-16T09:00:00Z',
    createdDate: '2024-05-20T14:00:00Z'
  },
  {
    id: 'contact_004',
    name: 'David Martinez',
    title: 'Finance Director',
    account: 'Financial Services Group',
    email: 'd.martinez@finservices.com',
    phone: '(555) 012-3456',
    mobile: '(555) 012-3457',
    status: 'Active',
    owner: 'Emily Davis',
    department: 'Finance',
    lastContact: '2025-01-13T16:45:00Z',
    createdDate: '2023-11-05T09:15:00Z'
  },
  {
    id: 'contact_005',
    name: 'Lisa Anderson',
    title: 'VP of Sales',
    account: 'Healthcare Innovations',
    email: 'l.anderson@healthcareinnovations.com',
    phone: '(555) 123-4567',
    mobile: '(555) 123-4568',
    status: 'Active',
    owner: 'David Wilson',
    department: 'Sales',
    lastContact: '2025-01-14T10:15:00Z',
    createdDate: '2024-01-08T11:30:00Z'
  },
  {
    id: 'contact_006',
    name: 'James Wilson',
    title: 'Senior Engineer',
    account: 'Tech Startup LLC',
    email: 'j.wilson@techstartup.io',
    phone: '(555) 234-5678',
    mobile: '(555) 234-5679',
    status: 'Active',
    owner: 'Sarah Johnson',
    department: 'Engineering',
    lastContact: '2025-01-12T09:30:00Z',
    createdDate: '2024-06-22T10:00:00Z'
  },
  {
    id: 'contact_007',
    name: 'Patricia Lee',
    title: 'HR Manager',
    account: 'Construction Pro LLC',
    email: 'p.lee@constructionpro.com',
    phone: '(555) 345-6789',
    mobile: '(555) 345-6790',
    status: 'Inactive',
    owner: 'Michael Chen',
    department: 'Human Resources',
    lastContact: '2024-12-15T13:00:00Z',
    createdDate: '2023-09-18T13:30:00Z'
  },
  {
    id: 'contact_008',
    name: 'Christopher Davis',
    title: 'Product Manager',
    account: 'Education Tech Solutions',
    email: 'c.davis@edtechsolutions.com',
    phone: '(555) 456-7890',
    mobile: '(555) 456-7891',
    status: 'Active',
    owner: 'John Smith',
    department: 'Product',
    lastContact: '2025-01-15T15:45:00Z',
    createdDate: '2024-07-30T14:00:00Z'
  },
  {
    id: 'contact_009',
    name: 'Michelle Brown',
    title: 'Customer Success Lead',
    account: 'Software Dynamics',
    email: 'm.brown@softwaredynamics.com',
    phone: '(555) 567-8901',
    mobile: '(555) 567-8902',
    status: 'Active',
    owner: 'Emily Davis',
    department: 'Customer Success',
    lastContact: '2025-01-16T10:00:00Z',
    createdDate: '2024-08-14T09:30:00Z'
  },
  {
    id: 'contact_010',
    name: 'Daniel Thompson',
    title: 'Legal Counsel',
    account: 'Legal Associates',
    email: 'd.thompson@legalassociates.com',
    phone: '(555) 678-9012',
    mobile: '(555) 678-9013',
    status: 'Active',
    owner: 'David Wilson',
    department: 'Legal',
    lastContact: '2025-01-11T16:30:00Z',
    createdDate: '2024-02-25T09:45:00Z'
  },
  {
    id: 'contact_011',
    name: 'Karen Rodriguez',
    title: 'Business Analyst',
    account: 'Global Industries Inc',
    email: 'k.rodriguez@globalind.com',
    phone: '(555) 789-0123',
    mobile: '(555) 789-0124',
    status: 'Active',
    owner: 'Sarah Johnson',
    department: 'Business Intelligence',
    lastContact: '2025-01-10T11:00:00Z',
    createdDate: '2024-09-05T10:15:00Z'
  },
  {
    id: 'contact_012',
    name: 'Thomas Miller',
    title: 'IT Director',
    account: 'Retail Solutions Co',
    email: 't.miller@retailsolutions.com',
    phone: '(555) 890-1234',
    mobile: '(555) 890-1235',
    status: 'Active',
    owner: 'Michael Chen',
    department: 'Information Technology',
    lastContact: '2025-01-13T10:15:00Z',
    createdDate: '2023-12-10T14:00:00Z'
  }
];

/**
 * Get available list views for contacts
 */
export function useContactListViews() {
  return useQuery({
    queryKey: ['contactListViews'],
    queryFn: async () => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      return persistedContactListViews.get();
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

const DEFAULT_PAGE_SIZE = 25;

/**
 * Get list view data (view configuration + records) with server-side pagination
 */
export function useContactListViewData(
  listViewId: string,
  options?: { page?: number; pageSize?: number }
) {
  const page = options?.page ?? 0;
  const pageSize = options?.pageSize ?? DEFAULT_PAGE_SIZE;
  const useBackend = shouldUseGobodo() || (USE_RDS_FOR_CORE_OBJECTS && isRdsCoreObject('Contact'));

  return useQuery<ListViewData>({
    queryKey: ['contactListViewData', listViewId, page, pageSize],
    queryFn: async () => {
      const allViews = persistedContactListViews.get();
      const listView = allViews.find(lv => lv.id === listViewId);
      if (!listView) throw new Error('List view not found');

      if (useBackend) {
        const { records: pageRecords, totalRecords } = await getRecordsForObjectPage('Contact', { page, size: pageSize });
        const seen = new Set<string>();
        let records: ListViewRecord[] = pageRecords
          .map(r => normalizeRecordForListView('Contact', r) as ListViewRecord)
          .filter(r => {
            if (seen.has(r.id)) return false;
            seen.add(r.id);
            return true;
          });

        if (listView.filters?.length) {
          listView.filters.forEach(filter => {
            records = records.filter(record => {
              const fieldValue = record[filter.fieldApiName];
              switch (filter.operator) {
                case 'equals':
                  if (filter.value === 'Current User') return fieldValue === 'John Smith';
                  return fieldValue === filter.value;
                case 'notEquals':
                  return fieldValue !== filter.value;
                case 'contains':
                  return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
                case 'startsWith':
                  return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
                default:
                  return true;
              }
            });
          });
        }

        if (listView.sortBy) {
          records = [...records].sort((a, b) => {
            const aValue = a[listView.sortBy!.fieldApiName];
            const bValue = b[listView.sortBy!.fieldApiName];
            if (listView.sortBy!.direction === 'asc') return aValue > bValue ? 1 : -1;
            return aValue < bValue ? 1 : -1;
          });
        }

        // Resolve client names only for account IDs on this page (no bulk Client__c fetch)
        const accountIds = Array.from(new Set(records.map(r => r.accountId ?? (r as any).AccountId__c).filter(Boolean))) as string[];
        const clientNameByAccountId: Record<string, string> = {};
        await Promise.all(
          accountIds.map(async (id) => {
            const client = await getRecordById('Client__c', id);
            if (client) {
              const name = (client.name ?? client.label ?? (client as any).Name__c) as string;
              if (name) clientNameByAccountId[id] = name;
            }
          })
        );
        const enrichedRecords = records.map(record => {
          const enriched = { ...record };
          const accountId = enriched.accountId ?? (enriched as any).AccountId__c;
          if (accountId && clientNameByAccountId[accountId]) {
            enriched.clientName = clientNameByAccountId[accountId];
          }
          return enriched;
        });
        return { listView, records: enrichedRecords, totalRecords };
      }

      const allClients = persistedClientRecords.get();
      const enrichWithClientName = (records: ListViewRecord[]) =>
        records.map(record => {
          const enriched = { ...record };
          const accountId = enriched.accountId ?? (enriched as any).AccountId__c;
          if (accountId) {
            const client = allClients.find((c: any) => c.id === accountId);
            if (client) {
              enriched.clientName = (client.name ?? client.label ?? (client as any).Name__c) as string;
            }
          }
          return enriched;
        });

      const allRecords = persistedContactRecords.get();
      let filtered: ListViewRecord[] = [...allRecords];
      if (listView.filters?.length) {
        listView.filters.forEach(filter => {
          filtered = filtered.filter(record => {
            const fieldValue = record[filter.fieldApiName];
            switch (filter.operator) {
              case 'equals':
                if (filter.value === 'Current User') return fieldValue === 'John Smith';
                return fieldValue === filter.value;
              case 'notEquals':
                return fieldValue !== filter.value;
              case 'contains':
                return String(fieldValue).toLowerCase().includes(String(filter.value).toLowerCase());
              case 'startsWith':
                return String(fieldValue).toLowerCase().startsWith(String(filter.value).toLowerCase());
              default:
                return true;
            }
          });
        });
      }
      if (listView.sortBy) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = a[listView.sortBy!.fieldApiName];
          const bValue = b[listView.sortBy!.fieldApiName];
          if (listView.sortBy!.direction === 'asc') return aValue > bValue ? 1 : -1;
          return aValue < bValue ? 1 : -1;
        });
      }
      const totalRecords = filtered.length;
      const start = page * pageSize;
      const records = enrichWithClientName(filtered.slice(start, start + pageSize));
      return { listView, records, totalRecords };
    },
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: !!listViewId,
    placeholderData: keepPreviousData,
  });
}

/**
 * Get default list view for contacts
 */
export function useDefaultContactListView() {
  return useQuery({
    queryKey: ['defaultContactListView'],
    queryFn: async () => {
      await new Promise(resolve => setTimeout(resolve, 200));

      const allViews = persistedContactListViews.get();
      const defaultView = allViews.find(lv => lv.isDefault);

      return defaultView || allViews[0];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Update a contact list view
 * Used by EditViewModal to persist changes
 */
export function useUpdateContactListView() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Partial<ListView> }) => {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));

      // Update in localStorage via mockDataManager
      const updated = persistedContactListViews.update(id, {
        ...updates,
        lastModifiedDate: new Date().toISOString()
      });

      if (!updated) {
        throw new Error('List view not found');
      }

      return updated;
    },
    onSuccess: (data) => {
      // Invalidate queries to trigger refetch
      queryClient.invalidateQueries({ queryKey: ['contactListViews'] });
      queryClient.invalidateQueries({ queryKey: ['contactListViewData', data.id] });
      queryClient.invalidateQueries({ queryKey: ['defaultContactListView'] });
    },
  });
}
